import { useState } from 'react';
import { Bot, Check, HardHat, Home, Mail, RotateCcw, Send } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Draft = { to: 'homeowner' | 'contractor'; toEmail: string | null; subject: string; html: string };

/**
 * RESUMEN DE PAGO por email (debajo de "qué hicimos con el dinero"): el HO o el contratista llaman
 * pidiendo aclaración del pago, y con un botón se genera con IA un email explicado "con manzanas"
 * (bilingüe EN/ES) con la barra del dinero recibido, los cheques, las facturas y el cuadre.
 * Se revisa el preview y se manda desde el correo de Payments; queda guardado en Payments
 * Department Updates, en el buzón del claim y pintado en el tracker.
 */
export function PaymentSummaryEmail({ claimId, onSent }: { claimId: string; onSent?: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState<'homeowner' | 'contractor'>('homeowner');
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  function fail(e: unknown) {
    const code = e instanceof ApiError ? e.message : '';
    setErr(
      code === 'no_homeowner_email' ? t('paysum.errNoHoEmail')
      : code === 'no_contractor_email' ? t('paysum.errNoCoEmail')
      : code === 'ai_not_configured' ? t('paysum.errNoAi')
      : code ? `Error: ${code}` : 'Error.',
    );
  }

  async function generate() {
    setBusy(true); setErr(null); setSentTo(null);
    try {
      const r = await api.post<Draft>(`/api/claims/${claimId}/payment-summary/draft`, { to, lang });
      setDraft(r);
      setSubject(r.subject);
    } catch (e) { fail(e); setDraft(null); } finally { setBusy(false); }
  }

  async function send() {
    if (!draft || !subject.trim()) return;
    if (!window.confirm(t('paysum.confirmSend'))) return;
    setSending(true); setErr(null);
    try {
      await api.post(`/api/claims/${claimId}/payment-summary/send`, { to, lang, subject: subject.trim(), html: draft.html });
      setSentTo(draft.toEmail ?? '');
      setDraft(null);
      onSent?.();
    } catch (e) { fail(e); } finally { setSending(false); }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--muted)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)' }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('paysum.title')}</span>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', flex: 1 }}>{t('paysum.subtitle')}</span>
      </div>

      {open && (
        <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
          {/* Destinatario + idioma */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4 }}>{t('paysum.sendTo')}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className={`btn sm${to === 'homeowner' ? '' : ' ghost'}`} onClick={() => { setTo('homeowner'); setDraft(null); }}><Home size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.ho')}</button>
                <button className={`btn sm${to === 'contractor' ? '' : ' ghost'}`} onClick={() => { setTo('contractor'); setDraft(null); }}><HardHat size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('tl.co')}</button>
              </div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4 }}>{t('paysum.language')}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className={`btn sm${lang === 'en' ? '' : ' ghost'}`} onClick={() => { setLang('en'); setDraft(null); }}>English</button>
                <button className={`btn sm${lang === 'es' ? '' : ' ghost'}`} onClick={() => { setLang('es'); setDraft(null); }}>Español</button>
              </div>
            </div>
          </div>

          {/* Botón GRANDE de generación con IA */}
          <button
            className="btn"
            disabled={busy}
            onClick={() => void generate()}
            style={{
              padding: '14px 20px', fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-bold)', borderColor: 'transparent',
              background: busy ? 'var(--muted)' : 'linear-gradient(90deg, var(--info), var(--green))', color: '#fff',
              borderRadius: 10, boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
            }}
          >
            <Bot size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {busy ? t('paysum.generating') : t('paysum.generate')}
          </button>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: -6 }}>{t('paysum.help')}</div>

          {err && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)' }}>{err}</div>}
          {sentTo !== null && (
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--green)', fontWeight: 'var(--fw-bold)' }}>
              <Check size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('paysum.sent')} {sentTo && <span className="muted" style={{ fontWeight: 400 }}>· {sentTo}</span>}
            </div>
          )}

          {/* Preview + envío */}
          {draft && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {t('paysum.willSendTo')}: <strong style={{ color: 'var(--ink)' }}>{draft.toEmail || '—'}</strong> · {t('paysum.fromPayments')}
              </div>
              <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t('paysum.subject')} />
              <iframe
                title="payment-summary-preview"
                srcDoc={draft.html}
                sandbox=""
                style={{ width: '100%', height: 460, border: '1px solid var(--line)', borderRadius: 8, background: '#fff' }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button className="btn ghost sm" disabled={busy || sending} onClick={() => void generate()}><RotateCcw size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('paysum.regenerate')}</button>
                <button className="btn ghost sm" disabled={sending} onClick={() => setDraft(null)}>{t('common.cancel')}</button>
                <button
                  className="btn sm"
                  disabled={sending || !subject.trim() || !draft.toEmail}
                  style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontWeight: 700 }}
                  onClick={() => void send()}
                >
                  {sending ? '…' : <><Send size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('paysum.send')}</>}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
